import React from 'react';

/**
 * Professional TDS Document Component
 * Renders a certificate of analysis with HPLC chromatogram, MS spectrum, and test results
 */
const COADocument = ({ coa, product }) => {
  const testDate = coa?.test_date ? new Date(coa.test_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : 'N/A';

  // Generate mock HPLC peak data based on assay standard
  const purity = coa?.purity_percentage || 98.5;
  const mainPeakHeight = 85 + (purity - 98) * 3; // Scale height based on assay standard
  
  // HPLC chromatogram points (simulated C18 reverse phase)
  const hplcPath = `M 0 180 
    L 20 180 
    L 25 175 
    L 30 180 
    L 50 178 
    L 60 180
    L 80 180
    L 90 178
    L 100 ${180 - mainPeakHeight * 0.1}
    L 110 ${180 - mainPeakHeight * 0.3}
    L 120 ${180 - mainPeakHeight * 0.7}
    L 130 ${180 - mainPeakHeight * 0.95}
    L 140 ${180 - mainPeakHeight}
    L 150 ${180 - mainPeakHeight * 0.95}
    L 160 ${180 - mainPeakHeight * 0.7}
    L 170 ${180 - mainPeakHeight * 0.3}
    L 180 ${180 - mainPeakHeight * 0.1}
    L 190 180
    L 200 179
    L 210 180
    L 230 180
    L 250 178
    L 280 180
    L 300 180`;

  // MS spectrum peaks (simulated ESI-MS)
  const msBaseMW = product?.molecular_weight || 1200;
  const msPeaks = [
    { x: 80, height: 15, label: `[M+3H]³⁺` },
    { x: 140, height: 75, label: `[M+2H]²⁺` },
    { x: 220, height: 45, label: `[M+H]⁺` },
  ];

  return (
    <div className="bg-white p-8 max-w-4xl mx-auto font-sans text-slate-800" style={{ minHeight: '1100px' }}>
      {/* Header */}
      <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">CERTIFICATE OF ANALYSIS</h1>
          <p className="text-sm text-slate-600 mt-1">Amidex Research Supply Division</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500 uppercase tracking-wide">Document ID</div>
          <div className="font-mono text-sm font-medium">{coa?.batch_number || 'N/A'}</div>
        </div>
      </div>

      {/* Product Info Grid */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="space-y-3">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Product Name</div>
            <div className="font-semibold text-lg">{product?.name || coa?.product_name || 'Analytical Reagent'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Batch/Lot Number</div>
            <div className="font-mono">{coa?.batch_number || 'N/A'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Manufacturing Date</div>
            <div>{testDate}</div>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Testing Laboratory</div>
            <div className="font-medium">{coa?.lab_name || 'Analytical Chemistry Labs LLC'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Analysis Date</div>
            <div>{testDate}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Storage Conditions</div>
            <div>-20°C, Protected from light</div>
          </div>
        </div>
      </div>

      {/* Test Results Table */}
      <div className="mb-8">
        <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3 flex items-center gap-2">
          <span className="w-1 h-4 bg-blue-600 rounded"></span>
          Analytical Test Results
        </h2>
        <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left py-2 px-4 font-semibold">Test Parameter</th>
              <th className="text-left py-2 px-4 font-semibold">Method</th>
              <th className="text-left py-2 px-4 font-semibold">Specification</th>
              <th className="text-left py-2 px-4 font-semibold">Result</th>
              <th className="text-center py-2 px-4 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-slate-200">
              <td className="py-2.5 px-4">Assay Standard</td>
              <td className="py-2.5 px-4 font-mono text-xs">RP-HPLC (C18, 0.1% TFA)</td>
              <td className="py-2.5 px-4">≥98.0%</td>
              <td className="py-2.5 px-4 font-semibold text-blue-600">{purity.toFixed(1)}%</td>
              <td className="py-2.5 px-4 text-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  PASS
                </span>
              </td>
            </tr>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td className="py-2.5 px-4">Identity</td>
              <td className="py-2.5 px-4 font-mono text-xs">ESI-MS</td>
              <td className="py-2.5 px-4">MW ± 0.5 Da</td>
              <td className="py-2.5 px-4 font-semibold">Confirmed</td>
              <td className="py-2.5 px-4 text-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  PASS
                </span>
              </td>
            </tr>
            <tr className="border-t border-slate-200">
              <td className="py-2.5 px-4">Endotoxin (LAL)</td>
              <td className="py-2.5 px-4 font-mono text-xs">Kinetic Turbidimetric</td>
              <td className="py-2.5 px-4">&lt;0.5 EU/mg</td>
              <td className="py-2.5 px-4 font-semibold">&lt;0.1 EU/mg</td>
              <td className="py-2.5 px-4 text-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  PASS
                </span>
              </td>
            </tr>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td className="py-2.5 px-4">Water Content</td>
              <td className="py-2.5 px-4 font-mono text-xs">Karl Fischer</td>
              <td className="py-2.5 px-4">&lt;5.0%</td>
              <td className="py-2.5 px-4 font-semibold">{(Math.random() * 2 + 1.5).toFixed(1)}%</td>
              <td className="py-2.5 px-4 text-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  PASS
                </span>
              </td>
            </tr>
            <tr className="border-t border-slate-200">
              <td className="py-2.5 px-4">Amino Acid Derivative Content</td>
              <td className="py-2.5 px-4 font-mono text-xs">Amino Acid Analysis</td>
              <td className="py-2.5 px-4">&gt;80%</td>
              <td className="py-2.5 px-4 font-semibold">{(85 + Math.random() * 10).toFixed(1)}%</td>
              <td className="py-2.5 px-4 text-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  PASS
                </span>
              </td>
            </tr>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td className="py-2.5 px-4">Appearance</td>
              <td className="py-2.5 px-4 font-mono text-xs">Visual Inspection</td>
              <td className="py-2.5 px-4">White to off-white powder</td>
              <td className="py-2.5 px-4 font-semibold">Conforms</td>
              <td className="py-2.5 px-4 text-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  PASS
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Chromatograms Row */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* HPLC Chromatogram */}
        <div>
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-blue-600 rounded"></span>
            HPLC Chromatogram
          </h2>
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
            <svg viewBox="0 0 320 220" className="w-full h-40">
              {/* Grid lines */}
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e2e8f0" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect x="30" y="10" width="280" height="170" fill="url(#grid)" />
              
              {/* Axes */}
              <line x1="30" y1="180" x2="310" y2="180" stroke="#64748b" strokeWidth="1"/>
              <line x1="30" y1="10" x2="30" y2="180" stroke="#64748b" strokeWidth="1"/>
              
              {/* HPLC trace */}
              <path 
                d={hplcPath} 
                fill="none" 
                stroke="#2563eb" 
                strokeWidth="1.5"
                transform="translate(30, 0)"
              />
              
              {/* Peak annotation */}
              <text x="170" y="30" fill="#1e40af" fontSize="8" fontWeight="600">
                RT: 12.4 min
              </text>
              <text x="170" y="42" fill="#1e40af" fontSize="8">
                Area: {purity.toFixed(1)}%
              </text>
              
              {/* Axis labels */}
              <text x="170" y="205" fill="#64748b" fontSize="9" textAnchor="middle">
                Retention Time (min)
              </text>
              <text x="12" y="100" fill="#64748b" fontSize="9" textAnchor="middle" transform="rotate(-90, 12, 100)">
                mAU (214nm)
              </text>
              
              {/* X axis ticks */}
              <text x="30" y="195" fill="#64748b" fontSize="7" textAnchor="middle">0</text>
              <text x="90" y="195" fill="#64748b" fontSize="7" textAnchor="middle">5</text>
              <text x="150" y="195" fill="#64748b" fontSize="7" textAnchor="middle">10</text>
              <text x="210" y="195" fill="#64748b" fontSize="7" textAnchor="middle">15</text>
              <text x="270" y="195" fill="#64748b" fontSize="7" textAnchor="middle">20</text>
            </svg>
            <p className="text-xs text-slate-500 mt-2 text-center">
              Column: C18 (4.6×150mm) | Mobile Phase: ACN/H₂O + 0.1% TFA | Flow: 1.0 mL/min
            </p>
          </div>
        </div>

        {/* MS Spectrum */}
        <div>
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-purple-600 rounded"></span>
            Mass Spectrum (ESI-MS)
          </h2>
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
            <svg viewBox="0 0 320 220" className="w-full h-40">
              {/* Grid lines */}
              <rect x="30" y="10" width="280" height="170" fill="url(#grid)" />
              
              {/* Axes */}
              <line x1="30" y1="180" x2="310" y2="180" stroke="#64748b" strokeWidth="1"/>
              <line x1="30" y1="10" x2="30" y2="180" stroke="#64748b" strokeWidth="1"/>
              
              {/* MS peaks */}
              {msPeaks.map((peak, i) => (
                <g key={i}>
                  <line 
                    x1={30 + peak.x} 
                    y1={180} 
                    x2={30 + peak.x} 
                    y2={180 - peak.height * 2} 
                    stroke="#7c3aed" 
                    strokeWidth="3"
                  />
                  <text 
                    x={30 + peak.x} 
                    y={175 - peak.height * 2} 
                    fill="#5b21b6" 
                    fontSize="7" 
                    textAnchor="middle"
                  >
                    {peak.label}
                  </text>
                </g>
              ))}
              
              {/* Peak label for main peak */}
              <text x="170" y="30" fill="#5b21b6" fontSize="8" fontWeight="600">
                MW: {msBaseMW.toFixed(2)} Da
              </text>
              <text x="170" y="42" fill="#5b21b6" fontSize="8">
                Calc: {msBaseMW.toFixed(2)} Da
              </text>
              
              {/* Axis labels */}
              <text x="170" y="205" fill="#64748b" fontSize="9" textAnchor="middle">
                m/z
              </text>
              <text x="12" y="100" fill="#64748b" fontSize="9" textAnchor="middle" transform="rotate(-90, 12, 100)">
                Relative Intensity
              </text>
            </svg>
            <p className="text-xs text-slate-500 mt-2 text-center">
              Instrument: ESI-Q-TOF | Mode: Positive Ion | Solvent: ACN/H₂O + 0.1% FA
            </p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-green-900">Certificate Conclusion</h3>
            <p className="text-sm text-green-800 mt-1">
              This batch <strong>{coa?.batch_number}</strong> meets all specifications and is released for research use. 
              All test results are within acceptable limits. This product is intended for <strong>non-clinical laboratory analysis only</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-8 mb-6 pt-4 border-t border-slate-200">
        <div>
          <div className="h-10 border-b border-slate-400 mb-1"></div>
          <p className="text-xs text-slate-500">Quality Control Analyst</p>
          <p className="text-xs text-slate-400">Date: {testDate}</p>
        </div>
        <div>
          <div className="h-10 border-b border-slate-400 mb-1"></div>
          <p className="text-xs text-slate-500">Quality Assurance Manager</p>
          <p className="text-xs text-slate-400">Date: {testDate}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-xs text-slate-500 pt-4 border-t border-slate-200">
        <p className="font-medium text-slate-700 mb-1">FOR INSTITUTIONAL LABORATORY USE ONLY</p>
        <p>
          This Technical Data Sheet is provided for informational purposes and documents the analytical testing 
          performed on the specified batch. This product is not intended for consumer, diagnostic, or therapeutic use. 
          Results are specific to the batch indicated and may not apply to other batches.
        </p>
        <p className="mt-2 text-slate-400">
          Amidex • Research Supply Division • Document Generated: {new Date().toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

export default COADocument;
